import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { findWhere, isEmpty, isNull, isUndefined } from "underscore";
import {
  ProductType,
  VisitorBookingProductConfiguration,
} from "../../../../../../../../../models/visitor-booking";
import { Fields } from "../ProductTimeframeForm.types";
import { VisitorBookingNewBooking } from "../../../../../models/VisitorBookingNewBooking";
import { FormikErrors } from "formik";
import { useAppContext } from '../../../../../../../../../context/hooks';
import { getTranslatedText } from '../productTranslation.utils';

export const useProductTimeframeFormBulk = (
  products: VisitorBookingProductConfiguration[],
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
  productId: string,
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<void> | Promise<FormikErrors<VisitorBookingNewBooking>>,
  physicalZone: string | null
) => {
  const { appState } = useAppContext();
  const { t } = useTranslation(["visitors"]);
  const [selectedProductId, setSelectedProductId] = useState(productId ?? "");
  const onProductSelect = (id?: string | number | undefined) => {
    setSelectedProductId((id as string) ?? "");
    setFieldValue(Fields.ProductId, id);
  };

  const selectedProduct = useMemo(() => {
    const found = findWhere(products, { id: selectedProductId });
    if (isUndefined(found)) {
      return null;
    }

    return found;
  }, [selectedProductId]);

  const isHourlyRateProduct = useMemo(
    () => !isNull(selectedProduct) && selectedProduct.productType === ProductType.HourlyRate,
    [selectedProduct]
    );

  const isMultiDayProduct = useMemo(
    () =>
        !isNull(selectedProduct) &&
          selectedProduct.productType === ProductType.MultiDay,
    [selectedProduct]
  );

  const isFlexFixedRateProduct = useMemo(
    () =>
        !isNull(selectedProduct) &&
        selectedProduct.isFlexibleEntryAllowed &&
        selectedProduct.productType === ProductType.FixedRate,
    [selectedProduct]
  );

  const allowedArrivalTimes = useMemo(
    () => {
      const times = selectedProduct?.allowedArrivalTimes ?? [];
      
      if (!selectedProduct?.isFlexibleEntryAllowed || isEmpty(startDate)) {
        return times;
      }
      
      const selectedDateMoment = moment(startDate, "DD/MM/YYYY");
      const today = moment().startOf('day');
      
      if (!selectedDateMoment.isSame(today, 'day')) {
        return times;
      }
      
      const now = moment();
      const filteredTimes = times.filter(time => {
        const [hours, minutes] = time.split(':').map(Number);
        const timeToday = moment().hours(hours).minutes(minutes).seconds(0);
        return timeToday.isAfter(now);
      });
      
      return filteredTimes;
    },
    [selectedProduct, startDate]
  );

  const maxRecurrence = useMemo(
    () => selectedProduct?.maxRecurrence ?? null,
    [selectedProduct]
  );

  const recurrenceOptions = useMemo(() => {
    if (!maxRecurrence) return [];
    
    return Array.from({ length: maxRecurrence }, (_, i) => {
      const value = i + 1;
      return {
        value,
        label: `${value} ${t(`productTimeframeForm.${value === 1 ? 'day' : 'day_plural'}`)}`
      };
    });
  }, [maxRecurrence, t]);

  const shouldDisableToday = useMemo(() => {
    if (!selectedProduct?.isFlexibleEntryAllowed) {
      return false;
    }
    
    const times = selectedProduct?.allowedArrivalTimes ?? [];
    if (times.length === 0) {
      return false;
    }
    
    const now = moment();
    const hasValidTime = times.some(time => {
      const [hours, minutes] = time.split(':').map(Number);
      const timeToday = moment().hours(hours).minutes(minutes).seconds(0);
      return timeToday.isAfter(now);
    });
    
    return !hasValidTime;
  }, [selectedProduct]); 

  const productManagementMinDate = useMemo(() => {
    if (isNull(selectedProduct) || !selectedProduct.isProductManagementProduct) {
      return null;
    }
    
    if (selectedProduct.startDate) {
      return moment(selectedProduct.startDate).toDate();
    }
    
    return null;
  }, [selectedProduct]);

  const productManagementMaxDate = useMemo(() => {
    if (isNull(selectedProduct) || !selectedProduct.isProductManagementProduct) {
      return null;
    }
    
    if (selectedProduct.endDate) {
      return moment(selectedProduct.endDate).toDate();
    }
    
    return null;
  }, [selectedProduct]);

  const setProductTypeAndParkingTime = useCallback(() => {
    if (isNull(selectedProduct)) {
      return;
    }
    const selectedLanguage = appState.selectedLanguage;
    
    setFieldValue(Fields.ProductType, selectedProduct?.productType);
    setFieldValue(Fields.ParkingTime, selectedProduct.parkingTime);
    setFieldValue(Fields.ProductName, getTranslatedText(selectedProduct.name, selectedProduct.nameTranslations, selectedLanguage));
    setFieldValue(Fields.IsProductManagementProduct, selectedProduct.isProductManagementProduct);
    setFieldValue(Fields.IsFlexibleEntryAllowed, selectedProduct.isFlexibleEntryAllowed);
  }, [selectedProduct, appState.selectedLanguage]);

  useEffect(() => setProductTypeAndParkingTime(), [setProductTypeAndParkingTime]);

  const updateTime = (field: Fields, time?: string) => {
    setFieldValue(field, !isUndefined(time) ? time : "");
  };

  const onStartTimeChange = (time: string) => {
    updateTime(Fields.StartTime, time);
  };

  const onEndTimeChange = (time: string) => {
    updateTime(Fields.EndTime, time);
  };

  const updateDate = (field: Fields, date: Date | null) => {
    setFieldValue(field, !isNull(date) ? moment(date).format("DD/MM/YYYY") : "");
  };

  const onStartDateChange = (date: Date | null) => {
    updateDate(Fields.StartDate, date);

    if (startDateAfterEndDate(date)) {
      updateDate(Fields.EndDate, null);
      updateTime(Fields.EndTime);
    }
  };

  const onEndDateChange = (date: Date | null) => {
    updateDate(Fields.EndDate, date);
  };

  const onRecurrenceChange = (recurrence: number | null) => {
    setFieldValue(Fields.Recurrence, recurrence);
  };

  const startDateAfterEndDate = (selectedStartDate: Date | null) => {
    if (isNull(startDate)) {
      return false;
    }

    if (isEmpty(endDate)) {
      return false;
    }

    const momentStartDate = moment(selectedStartDate);
    const momentEndDate = moment(endDate, "DD/MM/YYYY");

    return momentStartDate.isAfter(momentEndDate);
  };

  const jsStartDate = useMemo(() => {
    if (isEmpty(startDate)) {
      return null;
    }

    return moment(startDate, "DD/MM/YYYY").toDate();
  }, [startDate]);

  const jsEndDate = useMemo(() => {
    if (isEmpty(endDate)) {
      return null;
    }

    return moment(endDate, "DD/MM/YYYY").toDate();
  }, [endDate]);

  const jsMaxEndDate = useMemo(() => {
    if (isEmpty(startDate)) {
      return null;
    }

    const maxEndDate = moment(startDate, "DD/MM/YYYY").add(1, "year");

    return maxEndDate.toDate();
  }, [startDate]);

  const minStartTime = useMemo(() => {
    let result = "";
    if (isEmpty(startDate)) {
      return result;
    }

    let momentNow = moment(new Date());
    const countryCode = physicalZone?.split(".")[0];
    if (countryCode) {
      const zones = moment.tz.zonesForCountry(countryCode);
      momentNow = momentNow.tz(zones[0]);
    }
    const momentStartDate = moment(startDate, "DD/MM/YYYY");
    const momentStartTime = moment(startTime, "HH:mm");

    if (momentStartDate.isSameOrBefore(momentNow, "day")) {
      result = momentNow.format("HH:mm");

      if (
        momentStartDate.isBefore(momentNow, "day") ||
        (momentStartDate.isSame(momentNow, "day") &&
          momentStartTime.isSameOrBefore(momentNow, "minutes"))
      ) {
        updateTime(Fields.StartTime);
      }
    }

    return result;
  }, [startDate, physicalZone]);

  const minEndTime = useMemo(() => {
    if (isEmpty(startDate) || isEmpty(endDate) || isEmpty(startTime)) {
      return "";
    }

    const momentStartDate = moment(startDate, "DD/MM/YYYY");
    const momentStartTime = moment(startTime, "HH:mm");

    const momentEndDate = moment(endDate, "DD/MM/YYYY");
    const momentEndTime = moment(endTime, "HH:mm");

    if (
      (momentStartDate.isSameOrAfter(momentEndDate) && isEmpty(endTime)) ||
      (momentStartDate.isSameOrAfter(momentEndDate) && momentStartTime.isSameOrAfter(momentEndTime))
    ) {
      updateTime(Fields.EndTime);
      return startTime;
    }

    return "";
  }, [startDate, endDate, startTime]);

  const resetTimeframeOnProductChange = () => {
    updateDate(Fields.StartDate, null);
    updateTime(Fields.StartTime);
    updateDate(Fields.EndDate, null);
    updateTime(Fields.EndTime);
    setFieldValue(Fields.Recurrence, null);
  };

  useEffect(() => {
    if (isMultiDayProduct && recurrenceOptions.length > 0) {
      setFieldValue(Fields.Recurrence, recurrenceOptions[0].value);
    }
  }, [isMultiDayProduct, recurrenceOptions, setFieldValue]);

  return {
    jsStartDate,
    jsEndDate,
    jsMaxEndDate,
    isHourlyRateProduct,
    minEndTime,
    minStartTime,
    onStartDateChange,
    onEndDateChange,
    onStartTimeChange,
    onEndTimeChange,
    onRecurrenceChange,
    onProductSelect,
    isMultiDayProduct,
    isFlexFixedRateProduct,
    resetTimeframeOnProductChange,
    allowedArrivalTimes,
    maxRecurrence,
    recurrenceOptions,
    productManagementMinDate,
    productManagementMaxDate,
    shouldDisableToday,
  };
};
